import { useState } from "react";
import { Sparkles, X } from "lucide-react";
import { api, ApiError, type ScopeRefineOutput } from "../api";
import { Button, cx } from "../ui";

/**
 * "Refine with Elm" button + inline preview panel.
 *
 * Owner clicks the button, we POST the rough scope + selected
 * project type ids to /api/scope/refine, and Claude returns a
 * polished version with the rule-driven items inserted (acoustical
 * underlayment, GFCI, dielectric unions, etc.). The owner sees the
 * refined version in an editable textarea with a "Use this version"
 * button — they can also tweak the refined text before accepting.
 *
 * Renders nothing extra when ANTHROPIC_API_KEY is unset (the
 * endpoint returns null and we surface a one-line "Elm couldn't
 * refine this — leaving the original" message).
 */
export function ScopeRefineButton({
  scopeOfWork,
  projectTypeIds,
  unitId,
  flags,
  onAccept,
  disabled,
  className,
}: {
  scopeOfWork: string;
  projectTypeIds: string[];
  unitId?: string;
  flags?: {
    requiresRiserShutdown?: boolean;
    requiresTelecomShutdown?: boolean;
    isEmergency?: boolean;
  };
  onAccept: (refined: string) => void;
  disabled?: boolean;
  className?: string;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [output, setOutput] = useState<ScopeRefineOutput | null>(null);
  const [editing, setEditing] = useState("");

  const requestRefine = async () => {
    setBusy(true);
    setError(null);
    setOutput(null);
    try {
      const res = await api.post<{
        data: ScopeRefineOutput | null;
        reason?: string;
      }>("/api/scope/refine", {
        scopeOfWork,
        projectTypeIds,
        ...(unitId ? { unitId } : {}),
        ...(flags ? { flags } : {}),
      });
      if (!res.data) {
        setError(humanizeRefineFailure(res.reason));
        return;
      }
      setOutput(res.data);
      setEditing(res.data.refinedScope);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? typeof err.body === "object" && err.body && "error" in err.body
            ? String((err.body as { error: string }).error)
            : `HTTP ${err.status}`
          : String(err),
      );
    } finally {
      setBusy(false);
    }
  };

  const accept = () => {
    onAccept(editing);
    setOutput(null);
    setEditing("");
  };

  const dismiss = () => {
    setOutput(null);
    setEditing("");
    setError(null);
  };

  const humanizeRefineFailure = (reason: string | undefined): string => {
    switch (reason) {
      case "no_api_key":
        return "Elm's polish isn't configured for this environment. Your scope is unchanged.";
      case "max_tokens":
        return "Elm ran out of room polishing this scope. Try shortening it a little, or keep what you wrote.";
      case "no_tool_use_block":
      case "invalid_tool_input":
        return "Elm's polish came back malformed this time. Try again, or keep what you wrote.";
      case "fetch_failed":
        return "Couldn't reach Elm right now. Try again in a moment, or keep what you wrote.";
      default:
        return "Elm couldn't refine the scope right now. The original is unchanged — try again or keep what you wrote.";
    }
  };

  return (
    <div className={className}>
      {!output && !error && (
        <button
          type="button"
          onClick={() => void requestRefine()}
          disabled={
            disabled ||
            busy ||
            !scopeOfWork.trim() ||
            scopeOfWork.trim().length < 10 ||
            projectTypeIds.length === 0
          }
          className={cx(
            "inline-flex items-center gap-2 px-3 py-2 text-[12.5px] rounded-md transition-colors",
            disabled ||
              busy ||
              !scopeOfWork.trim() ||
              scopeOfWork.trim().length < 10
              ? "text-ink-400 bg-sand-50 cursor-not-allowed"
              : "text-brand-700 hover:text-brand-800 hover:bg-brand-50",
          )}
        >
          <Sparkles size={14} className="text-brand-600" />
          {busy ? "Polishing with Elm…" : "Polish with Elm"}
        </button>
      )}

      {error && (
        <div className="alert-error mt-2 flex items-start justify-between gap-3">
          <span>{error}</span>
          <button
            type="button"
            onClick={dismiss}
            className="text-ink-500 hover:text-ink-700 flex-shrink-0"
            aria-label="Dismiss"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {output && (
        <div className="mt-3 bg-white border border-brand-200 rounded-lg p-4 shadow-soft">
          <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
            <div>
              <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold tracking-wider uppercase text-brand-700">
                <Sparkles size={12} />
                Elm's polished version
              </div>
              <p className="text-[13px] text-ink-700 mt-1 leading-snug">
                {output.summary}
              </p>
            </div>
          </div>

          {output.addedItems.length > 0 && (
            <div className="mb-3">
              <div className="text-[11px] uppercase tracking-wider text-ink-400 mb-1">
                What Elm added
              </div>
              <ul className="flex flex-wrap gap-1.5">
                {output.addedItems.map((item, i) => (
                  <li
                    key={i}
                    className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] bg-brand-50 text-brand-800 border border-brand-100"
                  >
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <textarea
            className="textarea min-h-[260px] font-mono text-[12.5px] leading-relaxed"
            value={editing}
            onChange={(e) => setEditing(e.target.value)}
            rows={12}
          />
          <p className="text-[11.5px] text-ink-500 mt-2 leading-snug">
            Edit anything you want before accepting. Clicking "Use this
            version" replaces your scope with the text above.
          </p>
          <div className="flex gap-2 mt-3">
            <Button variant="primary" onClick={accept}>
              Use this version
            </Button>
            <Button variant="ghost" onClick={dismiss}>
              Keep original
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
